import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useCallback, useMemo } from "react";
import { ArrowRight, Mail, RotateCcw, Sparkles } from "lucide-react";
import type { SavedPhone, SavedRequester } from "@mtform/shared";
import { useAuth } from "@/auth/AuthContext";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { PhoneField } from "@/components/form/PhoneField";
import { SavedValuePicker } from "@/components/form/SavedValuePicker";
import { formatUsPhone } from "@/lib/format";
import { WizardCard } from "./WizardCard";
import { usePatientLookupState } from "./PatientLookupContext";
import type { TripFormValues } from "./schema";
import type { WizardStepStatus } from "./WizardCard";

interface RequesterSectionProps {
  index: number;
  status: WizardStepStatus;
  onContinue: () => void;
  onEdit: () => void;
}

export function RequesterSection({
  index,
  status,
  onContinue,
  onEdit,
}: RequesterSectionProps) {
  const {
    control,
    register,
    setValue,
    formState: { errors },
    trigger,
  } = useFormContext<TripFormValues>();

  const lookup = usePatientLookupState();
  const auth = useAuth();
  const me =
    auth.state.status === "authenticated" ? auth.state.user : null;
  const requestedByWatch = useWatch({ control, name: "requestedBy" }) ?? "";
  const callbackPhoneWatch = useWatch({ control, name: "callbackPhone" }) ?? "";
  const requesterEmailWatch = useWatch({ control, name: "requesterEmail" }) ?? "";

  // NOTE: The actual auto-prefill from the logged-in user's profile is
  // owned by `TripForm`. It bakes the three requester fields into the
  // form's defaults so *every* reset path (initial mount, draft restore,
  // "wrong patient", submit→restart) starts with Requester pre-filled.
  // This file only reads the live values to drive the match-state
  // affordances below.

  // Match-state for the three fields against the logged-in user's
  // profile. We surface two different affordances based on the
  // relationship between what's on screen and what's on the account:
  //
  //   * If at least one field already matches the user's profile and
  //     none of the three diverges → "Pre-filled from your account"
  //     informational badge. Reassures the user that the values came
  //     from their account so they don't second-guess them.
  //
  //   * If at least one field has been changed away from the profile
  //     value (typed over, picked from saved-requesters for a returning
  //     patient, etc.) → "Reset to my account info" button. One click
  //     restores all three fields to the account defaults — useful when
  //     a dispatcher booked on behalf of someone else for the previous
  //     trip and wants to swap back to themselves.
  const profileFullName = me
    ? `${me.firstName ?? ""} ${me.lastName ?? ""}`.trim()
    : "";
  const requestedByMatches =
    Boolean(me) && profileFullName !== "" &&
    normalizeText(requestedByWatch) === normalizeText(profileFullName);
  const callbackPhoneMatches =
    Boolean(me?.phone) &&
    digitsOnly(callbackPhoneWatch) === digitsOnly(me?.phone ?? "");
  const requesterEmailMatches =
    Boolean(me?.email) &&
    normalizeText(requesterEmailWatch) === normalizeText(me?.email ?? "");

  const anyMatchesProfile =
    requestedByMatches || callbackPhoneMatches || requesterEmailMatches;
  const anyDivergesFromProfile =
    Boolean(me) &&
    ((profileFullName !== "" &&
      requestedByWatch.trim() !== "" &&
      !requestedByMatches) ||
      (Boolean(me?.phone) &&
        callbackPhoneWatch.trim() !== "" &&
        !callbackPhoneMatches) ||
      (Boolean(me?.email) &&
        requesterEmailWatch.trim() !== "" &&
        !requesterEmailMatches));

  const showAutofillBadge = anyMatchesProfile && !anyDivergesFromProfile;
  const showResetButton = anyDivergesFromProfile;

  const onResetToAccount = useCallback(() => {
    if (!me) return;
    const fullName = `${me.firstName ?? ""} ${me.lastName ?? ""}`.trim();
    setValue("requestedBy", fullName, { shouldDirty: true, shouldTouch: true });
    setValue("callbackPhone", me.phone ? formatUsPhone(me.phone) : "", {
      shouldDirty: true,
      shouldTouch: true,
    });
    setValue("requesterEmail", me.email ?? "", {
      shouldDirty: true,
      shouldTouch: true,
    });
  }, [me, setValue]);

  const allSavedRequesters =
    lookup.status === "found" && lookup.patient ? lookup.patient.savedRequesters : [];
  const savedRequesters = useMemo(
    () =>
      allSavedRequesters.filter(
        (s) =>
          normalizeText(s.requestedBy) !== normalizeText(requestedByWatch) ||
          digitsOnly(s.callbackPhone) !== digitsOnly(callbackPhoneWatch) ||
          normalizeText(s.requesterEmail) !== normalizeText(requesterEmailWatch),
      ),
    [allSavedRequesters, requestedByWatch, callbackPhoneWatch, requesterEmailWatch],
  );

  const allSavedCallbackPhones =
    lookup.status === "found" && lookup.patient
      ? lookup.patient.savedCallbackPhones
      : [];
  const savedCallbackPhones = useMemo(
    () =>
      allSavedCallbackPhones.filter(
        (s) => digitsOnly(s.phone) !== digitsOnly(callbackPhoneWatch),
      ),
    [allSavedCallbackPhones, callbackPhoneWatch],
  );

  const renderSavedRequester = useCallback(
    (s: SavedRequester) => ({
      primary: s.requestedBy,
      meta: [s.callbackPhone ? formatUsPhone(s.callbackPhone) : "", s.requesterEmail]
        .filter(Boolean)
        .join(" · "),
    }),
    [],
  );

  const onSelectSavedRequester = useCallback(
    (s: SavedRequester) => {
      setValue("requestedBy", s.requestedBy, { shouldDirty: true, shouldTouch: true });
      if (s.callbackPhone) {
        setValue("callbackPhone", formatUsPhone(s.callbackPhone), {
          shouldDirty: true,
          shouldTouch: true,
        });
      }
      if (s.requesterEmail) {
        setValue("requesterEmail", s.requesterEmail, {
          shouldDirty: true,
          shouldTouch: true,
        });
      }
    },
    [setValue],
  );

  const renderSavedPhone = useCallback(
    (s: SavedPhone) => ({ primary: formatUsPhone(s.phone) }),
    [],
  );

  const onSelectSavedCallbackPhone = useCallback(
    (s: SavedPhone) => {
      setValue("callbackPhone", formatUsPhone(s.phone), {
        shouldDirty: true,
        shouldTouch: true,
      });
    },
    [setValue],
  );

  async function handleContinue() {
    const ok = await trigger([
      "requestedBy",
      "callbackPhone",
      "requesterEmail",
    ]);
    if (!ok) return;
    onContinue();
  }

  if (status === "complete") {
    const summary = requestedByWatch
      ? `${requestedByWatch}${callbackPhoneWatch ? ` · ${callbackPhoneWatch}` : ""}`
      : "—";
    return (
      <WizardCard
        index={index}
        title="Requester"
        status="complete"
        summary={summary}
        onEdit={onEdit}
      />
    );
  }

  return (
    <WizardCard
      index={index}
      title="Requester"
      description="Final contact details for confirmation and coordination."
      status={status}
    >
      {showAutofillBadge ? (
        <p className="-mt-1 inline-flex items-center gap-1.5 justify-self-start rounded-full border border-primary/40 bg-primary/10 px-2.5 py-0.5 text-[0.65rem] font-semibold text-primary">
          <Sparkles className="h-3 w-3" /> Pre-filled from your account — edit as needed.
        </p>
      ) : null}

      {showResetButton ? (
        <button
          type="button"
          onClick={onResetToAccount}
          className="-mt-1 inline-flex items-center gap-1.5 justify-self-start rounded-full border border-foreground/15 bg-foreground/[0.04] px-2.5 py-0.5 text-[0.65rem] font-semibold text-muted-foreground transition hover:border-primary/45 hover:bg-primary/10 hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          title="Reset Requested by, Call back #, and Email to the values saved on your account."
        >
          <RotateCcw className="h-3 w-3" /> Reset to my account info
        </button>
      ) : null}

      {savedRequesters.length > 0 ? (
        <div className="flex justify-end">
          <SavedValuePicker
            label="Saved requesters"
            items={savedRequesters}
            renderItem={renderSavedRequester}
            onSelect={onSelectSavedRequester}
            description="Click a person to fill name, callback phone, and email together."
          />
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Field label="Requested by" required htmlFor="requestedBy" error={errors.requestedBy?.message}>
          <Input
            id="requestedBy"
            maxLength={100}
            clearable
            {...register("requestedBy")}
          />
        </Field>
        <div className="flex flex-col gap-1.5">
          <Field
            label="Call back #"
            required
            htmlFor="callbackPhone"
            error={errors.callbackPhone?.message}
          >
            <Controller
              control={control}
              name="callbackPhone"
              render={({ field, fieldState }) => (
                <PhoneField
                  id="callbackPhone"
                  value={field.value ?? ""}
                  onChange={field.onChange}
                  placeholder="(646) 555-0133"
                  invalid={Boolean(fieldState.error)}
                />
              )}
            />
          </Field>
          {savedCallbackPhones.length > 0 ? (
            <SavedValuePicker
              label="Saved callbacks"
              items={savedCallbackPhones}
              renderItem={renderSavedPhone}
              onSelect={onSelectSavedCallbackPhone}
            />
          ) : null}
        </div>
      </div>

      <Field
        label="Email"
        htmlFor="requesterEmail"
        error={errors.requesterEmail?.message}
        hint={
          <span className="inline-flex items-center gap-1 text-primary">
            <Mail className="h-3.5 w-3.5" />
            We'll send the trip confirmation here — change it if a different person should get it.
          </span>
        }
      >
        <Input
          id="requesterEmail"
          type="email"
          autoComplete="email"
          placeholder="your@email.com"
          maxLength={100}
          clearable
          {...register("requesterEmail")}
        />
      </Field>

      <div className="flex justify-end pt-1">
        <Button type="button" onClick={handleContinue} className="min-w-[10rem]">
          Continue to review
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </WizardCard>
  );
}

function normalizeText(value: string | null | undefined): string {
  return String(value ?? "").trim().toLowerCase();
}

function digitsOnly(value: string | null | undefined): string {
  return String(value ?? "").replace(/\D/gu, "");
}
